import { useState } from 'react'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons'
import { Alert, Button, Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { PendingDots, StatusRow } from '../StatusRow'
import { useWaitPoll } from '../../hooks/useWaitPoll'
import {
  byMerchant,
  fetchAutoCategorizationStatus,
  POLL_SCHEDULE,
  toLabel,
  toOutcome,
  toShownOf,
} from './waitForAutoCategorization'
import type { WidgetProps } from './types'

export const SORTING_LABEL = 'Sorting your business transactions'
export const STILL_SORTING_LABEL = 'Still sorting your business transactions'

export const WaitForAutoCategorizationWidget = ({
  resolved,
  onOutcome,
}: WidgetProps) => {
  const [expanded, setExpanded] = useState(false)
  const { status, timedOut, loadFailed, retry } = useWaitPoll({
    fetchStatus: fetchAutoCategorizationStatus,
    schedule: POLL_SCHEDULE,
    resolved,
    onSettled: (next, late) => onOutcome(toOutcome(next, late)),
  })

  if (loadFailed) {
    return (
      <Alert
        variant="error"
        description="Heard couldn't check on your categorization right now."
        ctaText="Try again"
        ctaOnClick={retry}
      />
    )
  }

  if (!status || (status.pending && !timedOut && !resolved)) {
    return (
      <StatusRow label={SORTING_LABEL} pending trailing={<PendingDots />} />
    )
  }

  if (status.pending) {
    return <StatusRow label={STILL_SORTING_LABEL} pending trailing={null} />
  }

  const { categorized, categorizedCount } = status
  const shownOf = toShownOf(status)

  return (
    <StatusRow
      label={toLabel(categorizedCount)}
      pending={false}
      singleLine
      trailing={
        <>
          <Icon
            icon={faCircleCheck}
            color="text-green-100"
            className="text-xs"
          />
          {categorized.length > 0 && (
            <Button
              variant="link"
              aria-label={expanded ? 'Hide transactions' : 'Show transactions'}
              className="h-6 w-6 p-0"
              onClick={() => setExpanded(!expanded)}
            >
              <Icon
                icon={expanded ? faChevronUp : faChevronDown}
                color="text-neutral-400"
                className="text-xs"
              />
            </Button>
          )}
        </>
      }
    >
      {expanded && (
        <div className="mt-2 pl-6">
          {byMerchant(categorized).map((transaction) => (
            <div
              key={transaction.id}
              className="flex items-baseline gap-3 border-t border-neutral-50 py-2 first:border-t-0"
            >
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.MUTED}
                className="w-24 shrink-0"
              >
                {transaction.date}
              </Text>
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.HEADER}
                className="min-w-0 flex-1 truncate"
              >
                {transaction.merchant}
              </Text>
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.MUTED}
                className="shrink-0"
              >
                {transaction.category}
              </Text>
            </div>
          ))}
          {shownOf && (
            <Text
              variant={TypographyStyles.BODY_XS}
              color={TextColors.MUTED}
              className="border-t border-neutral-50 py-2"
            >
              {shownOf}
            </Text>
          )}
        </div>
      )}
    </StatusRow>
  )
}
